import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { testimonials } from '../data/mockData';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section 
      ref={sectionRef} 
      className="py-20 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f5f5f5 100%)'
      }}
    >
      {/* Subtle Pattern */}
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, rgba(0,0,0,0.03) 1px, transparent 0)`,
        backgroundSize: '32px 32px'
      }} />

      <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-12 transform transition-all duration-1000 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <span className="inline-block px-4 py-2 bg-gray-100 rounded-full text-gray-500 text-sm font-medium mb-4">
            Client Success Stories
          </span>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
            What Our Clients Say
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-sm">
            Trusted by hundreds of clients to manage their wealth and achieve their financial goals
          </p>
        </div>

        {/* Testimonials Carousel - Fixed Height Landscape Layout */}
        <div className={`transform transition-all duration-1000 delay-200 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          {/* Main Testimonial Card - Fixed Static Height */}
          <div className="relative bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden" style={{ height: '320px' }}>
            <div className="absolute top-4 right-4">
              <Quote className="w-12 h-12 text-gray-100" />
            </div>
            
            <div className="p-6 lg:p-8 h-full flex flex-col lg:flex-row items-start lg:items-center gap-6">
              {/* Client Image */}
              <div className="flex-shrink-0">
                <div className="relative">
                  <img
                    src={testimonials[currentIndex].image}
                    alt={testimonials[currentIndex].name}
                    className="w-20 h-20 lg:w-28 lg:h-28 rounded-xl object-cover border-2 border-gray-200 shadow-md"
                  />
                  <div className="absolute -bottom-2 -right-2 w-7 h-7 bg-emerald-500 rounded-full flex items-center justify-center shadow-md">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Testimonial Content */}
              <div className="flex-1 flex flex-col justify-center overflow-hidden">
                <p className="text-gray-700 text-sm lg:text-base leading-relaxed mb-5 overflow-y-auto" style={{ maxHeight: '160px' }}>
                  "{testimonials[currentIndex].quote}"
                </p>
                <div className="flex-shrink-0">
                  <h4 className="text-gray-900 font-bold text-base lg:text-lg">{testimonials[currentIndex].name}</h4>
                  <p className="text-gray-500 text-sm">{testimonials[currentIndex].role}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={prevTestimonial}
              className="p-2.5 bg-white rounded-full border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-300 shadow-sm"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>

            {/* Progress Dots */}
            <div className="flex items-center gap-2">
              {testimonials.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`transition-all duration-500 rounded-full ${
                    idx === currentIndex
                      ? 'w-6 h-2 bg-gray-900'
                      : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={nextTestimonial}
              className="p-2.5 bg-white rounded-full border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-300 shadow-sm"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Client Avatars Preview */}
          <div className="flex items-center justify-center gap-3 mt-6">
            <div className="flex -space-x-2">
              {testimonials.map((testimonial, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`relative transition-all duration-300 ${
                    idx === currentIndex ? 'z-10 scale-110' : 'hover:scale-105'
                  }`}
                >
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className={`w-10 h-10 rounded-full object-cover border-2 transition-all duration-300 shadow-sm ${
                      idx === currentIndex 
                        ? 'border-emerald-500' 
                        : 'border-white opacity-60 hover:opacity-100'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
